import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLSelectElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const select = this.refs.quantityInput;
    if (!(select instanceof HTMLSelectElement)) return;

    if (select.selectedIndex < select.options.length - 1) {
      select.selectedIndex += 1;
      this.#onQuantityChange(event);
    }
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const select = this.refs.quantityInput;
    if (!(select instanceof HTMLSelectElement)) return;

    if (select.selectedIndex > 0) {
      select.selectedIndex -= 1;
      this.#onQuantityChange(event);
    }
  }

  /**
   * (No-op for select) Kept for API compatibility.
   * @param {FocusEvent} _event
   */
  selectInputValue(_event) {
    // Not needed for <select>, but preserved to avoid breaking references.
  }

  /**
   * Handles the quantity set (change) event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLSelectElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} _event - The event.
   */
  #onQuantityChange(_event) {
    const { quantityInput } = this.refs;
    this.#enforceRange();

    const newValue = parseInt(quantityInput.value, 10);
    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine))
    );
  }

  /**
   * Ensures the selected option stays within 1..15.
   */
  #enforceRange = () => {
    const select = this.refs.quantityInput;
    if (!(select instanceof HTMLSelectElement)) return;

    // Options are fixed 1..15; clamp index just in case.
    if (select.selectedIndex < 0) select.selectedIndex = 0;
    if (select.selectedIndex > select.options.length - 1) {
      select.selectedIndex = select.options.length - 1;
    }
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLSelectElement} The quantity select.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <select ref="quantityInput" /> inside <quantity-selector-component />');
    }
    return /** @type {HTMLSelectElement} */ (this.refs.quantityInput);
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
